import { useState, useEffect, useCallback } from "react";
import * as React from "react";
import { runtimeConfig } from "src/services/runtimeConfig";
import { useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios, { AxiosProgressEvent } from "axios";
import { HttpMethod, callApi } from "src/services/apiService";
import Loading from "src/components/Loading";
import { useUploadListContext } from "src/contexts/UploadListContext";
import FileUploadContainer from "src/components/FileUploadContainer";
import ProgressBar from "src/components/ProgressBar";
import Warning from "src/components/Warning";
import ToolTip from "src/components/ToolTip";
import { CDRSession } from "./types";
import Sessions from "./Sessions";
import CollapsibleCard from "src/components/CollapsibleCard";
import CDRMapping from "./CDRMapping";
import { v4 as uuidv4 } from "uuid";
import { useSignalR } from "src/hooks/useSignalR";
import { toast } from "sonner";

// Utility function to parse analysis results
const parseAnalysisResults = (
  analysisResults: any,
): { analysisResults: any; isProcessing: boolean } => {
  if (analysisResults === null) {
    return { analysisResults: null, isProcessing: true };
  }

  try {
    if (typeof analysisResults === "string") {
      return {
        analysisResults: JSON.parse(analysisResults),
        isProcessing: false,
      };
    } else if (analysisResults) {
      return { analysisResults, isProcessing: false };
    }
  } catch (parseError) {
    console.error("Error parsing analysis JSON", parseError);
  }

  return { analysisResults: null, isProcessing: false };
};

// Helper function to parse duration string to total minutes for sorting
const parseDurationToMinutes = (duration: string): number => {
  // Handle format like "1.00:00:00" (1 day)
  if (duration.includes(".")) {
    const [days, time] = duration.split(".");
    const [hours, minutes] = time.split(":").map(Number);
    return parseInt(days) * 24 * 60 + hours * 60 + minutes;
  }

  // Handle format like "01:00:00" or "00:15:00"
  const parts = duration.split(":").map(Number);
  if (parts.length === 3) {
    const [hours, minutes] = parts;
    return hours * 60 + minutes;
  }

  // Fallback for unknown formats
  return 0;
};

// Utility function to get duration violations as dynamic array
const getDurationViolations = (analysisResults: any) => {
  if (!analysisResults?.MaximumCallLimitViolations) {
    return [];
  }

  const violations = analysisResults.MaximumCallLimitViolations.map(
    (limit: { Duration: string; Threshold: number; Violations: number }) => {
      // Generate display name and description based on duration and threshold
      const { displayName, description } = getViolationDisplayInfo(
        limit.Duration,
        limit.Threshold,
      );

      return {
        duration: limit.Duration,
        threshold: limit.Threshold,
        violations: limit.Violations,
        displayName,
        description,
      };
    },
  );

  // Sort by time period (smallest first), then by threshold (smallest first)
  return violations.sort((a: any, b: any) => {
    const durationA = parseDurationToMinutes(a.duration);
    const durationB = parseDurationToMinutes(b.duration);

    // First sort by duration (smallest time period first)
    if (durationA !== durationB) {
      return durationA - durationB;
    }

    // If durations are equal, sort by threshold (smallest number of calls first)
    return a.threshold - b.threshold;
  });
};

// Helper function to parse C# TimeSpan format and generate human-readable labels
// Supports formats: "d.hh:mm:ss", "hh:mm:ss", "mm:ss", "ss"
// Examples: "1.00:00:00" (1 day), "01:30:00" (1.5 hours), "15:00" (15 min), "30" (30 sec)
const parseTimeSpanToLabel = (duration: string) => {
  try {
    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    // Handle format with days: "d.hh:mm:ss"
    if (duration.includes(".")) {
      const [daysPart, timePart] = duration.split(".");
      days = parseInt(daysPart, 10);
      const [h, m, s] = timePart.split(":").map(Number);
      hours = h;
      minutes = m;
      seconds = s;
    } else {
      // Handle format without days: "hh:mm:ss" or "mm:ss"
      const parts = duration.split(":").map(Number);
      if (parts.length === 3) {
        [hours, minutes, seconds] = parts;
      } else if (parts.length === 2) {
        [minutes, seconds] = parts;
      }
    }

    // Convert to the most appropriate unit and generate labels
    const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
    const totalHours = days * 24 + hours;

    // Check if we actually parsed anything meaningful
    if (totalMinutes === 0 && seconds === 0) {
      throw new Error("No meaningful time components found");
    }

    if (days > 0) {
      // Show as hours when we have days (e.g., "24-Hour")
      const displayLabel = `${totalHours}-Hour`;
      const descriptionPeriod =
        totalHours === 1 ? "1 hour" : `${totalHours} hours`;
      return { displayLabel, descriptionPeriod, showThreshold: true };
    } else if (hours > 0) {
      // Show as hours (e.g., "1-Hour")
      const displayLabel = `${hours}-Hour`;
      const descriptionPeriod = hours === 1 ? "1 hour" : `${hours} hours`;
      return { displayLabel, descriptionPeriod, showThreshold: false };
    } else if (minutes > 0) {
      // Show as minutes (e.g., "15-Minute")
      const displayLabel = `${minutes}-Minute`;
      const descriptionPeriod =
        minutes === 1 ? "1 minute" : `${minutes} minutes`;
      return { displayLabel, descriptionPeriod, showThreshold: false };
    } else if (seconds > 0) {
      // Show as seconds (e.g., "30-Second")
      const displayLabel = `${seconds}-Second`;
      const descriptionPeriod =
        seconds === 1 ? "1 second" : `${seconds} seconds`;
      return { displayLabel, descriptionPeriod, showThreshold: false };
    } else {
      // Shouldn't reach here due to check above, but fallback just in case
      throw new Error("Invalid time duration");
    }
  } catch (error) {
    // Fallback for unparseable formats
    return {
      displayLabel: duration,
      descriptionPeriod: duration,
      showThreshold: true,
    };
  }
};

// Helper function to generate display names and descriptions for violations
const getViolationDisplayInfo = (duration: string, threshold: number) => {
  const { displayLabel, descriptionPeriod, showThreshold } =
    parseTimeSpanToLabel(duration);

  const displayName = showThreshold
    ? `${displayLabel} Limit (${threshold}+)`
    : `${displayLabel} Limit`;

  return {
    displayName,
    description: `Called more than ${threshold} times within ${descriptionPeriod}`,
  };
};

// Utility function to transform call dispositions
const transformCallDispositions = (callDispositionCounts: any) => {
  return {
    total: Object.values(callDispositionCounts).reduce(
      (sum: number, count: any) => sum + (Number(count) || 0),
      0,
    ) as number,
    connected: Number(
      callDispositionCounts.Connected || callDispositionCounts.connected || 0,
    ),
    noAnswer: Number(
      callDispositionCounts.NoAnswer || callDispositionCounts.noAnswer || 0,
    ),
    voicemail: Number(
      callDispositionCounts.Voicemail || callDispositionCounts.voicemail || 0,
    ),
    wrongNumber: Number(
      callDispositionCounts.WrongNumber ||
        callDispositionCounts.wrongNumber ||
        0,
    ),
    other: Number(
      callDispositionCounts.Other || callDispositionCounts.other || 0,
    ),
  };
};

// Utility function to transform calls per day ANI data
const transformCallsPerDayANI = (callsPerDayANIFromApi: any) => {
  const transformedCallsPerDayANI: {
    dates: string[];
    data: { ani: string; callCounts: number[] }[];
  } = { dates: [], data: [] };

  const uniqueDates = new Set<string>();
  Object.values(callsPerDayANIFromApi).forEach((aniData: any) => {
    if (Array.isArray(aniData)) {
      aniData.forEach((dayEntry: any) => {
        if (dayEntry && dayEntry.Item1) {
          uniqueDates.add(new Date(dayEntry.Item1).toLocaleDateString());
        }
      });
    }
  });

  transformedCallsPerDayANI.dates = Array.from(uniqueDates).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  Object.entries(callsPerDayANIFromApi).forEach(
    ([ani, aniData]: [string, any]) => {
      const callCounts = transformedCallsPerDayANI.dates.map((dateStr) => {
        const foundEntry = Array.isArray(aniData)
          ? aniData.find(
              (d: any) =>
                d && new Date(d.Item1).toLocaleDateString() === dateStr,
            )
          : null;
        return foundEntry && foundEntry.Item2 ? Number(foundEntry.Item2) : 0;
      });
      transformedCallsPerDayANI.data.push({ ani, callCounts });
    },
  );

  return transformedCallsPerDayANI;
};

// Utility function to calculate summary data
const calculateSummaryData = (sessions: CDRSession[]) => {
  const nonProcessingSessions = sessions.filter(
    (session) => !session.isProcessing,
  );
  const totalCalls = nonProcessingSessions.reduce(
    (sum, session) => sum + (session.totalCalls || 0),
    0,
  );
  const connectedCalls = nonProcessingSessions.reduce(
    (sum, session) => sum + (session.connectedCalls || 0),
    0,
  );
  const averageDurationOverall =
    nonProcessingSessions.length > 0 ? "Calc Needed" : "N/A";
  const successRateOverall =
    totalCalls > 0
      ? `${((connectedCalls / totalCalls) * 100).toFixed(0)}%`
      : "N/A";

  return {
    totalCalls,
    connectedCalls,
    averageDuration: averageDurationOverall,
    successRate: successRateOverall,
  };
};

// Utility function to create default data structure
const createDefaultDataStructure = (sessions: CDRSession[] = []) => {
  return {
    title: "CDR Analysis",
    summary: calculateSummaryData(sessions),
    sessions: sessions.sort(
      (a, b) =>
        new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime(),
    ),
  };
};

// Utility function to create CDR analysis submit payload
const createSubmitPayload = (
  campaignId: string,
  fileNameWithGuid: string,
  columnMappings: Record<string, string>,
  hasHeader: boolean,
) => {
  // columnMappings is { fieldId: fileColumnName }, e.g. { PhoneNumber: "Destination Number" }
  // Backend expects columnSchemaMapping as { fieldId: fileColumnName }
  return {
    campaignId: parseInt(campaignId),
    blobFile: fileNameWithGuid,
    uploadedDate: new Date().toISOString(),
    columnSchemaMapping: columnMappings,
    callDispositionMapping: {
      connected: "Connected",
      "Connected Call": "Connected",
      answer: "Connected",
      noanswer: "NoAnswer",
      "no answer": "NoAnswer",
      "No Response": "NoAnswer",
      voicemail: "Voicemail",
      "left voicemail": "Voicemail",
      busy: "Busy",
      "line busy": "Busy",
      failed: "Failed",
      error: "Failed",
      "": "Unknown",
      unknown: "Unknown",
    },
    maximumCallLimits: [
      {
        Threshold: 2,
        Duration: "00:05:00",
        Violations: 0,
        PhoneNumbers: [],
      },
      {
        Threshold: 9,
        Duration: "1.00:00:00",
        Violations: 0,
        PhoneNumbers: [],
      },
      {
        Threshold: 5,
        Duration: "1.00:00:00",
        Violations: 0,
        PhoneNumbers: [],
      },
      {
        Threshold: 3,
        Duration: "1.00:00:00",
        Violations: 0,
        PhoneNumbers: [],
      },
      {
        Threshold: 3,
        Duration: "01:00:00",
        Violations: 0,
        PhoneNumbers: [],
      },
      {
        Threshold: 2,
        Duration: "00:15:00",
        Violations: 0,
        PhoneNumbers: [],
      },
    ],
    hasHeader,
  };
};

// Main utility function to transform session data
const transformSessionData = (session: any): CDRSession => {
  const { analysisResults, isProcessing } = parseAnalysisResults(
    session.analysisResults,
  );

  const baseSession = {
    errorMessage: session.errorMessage,
    sessionId: session.id,
    fileName: session.blobFile,
    uploadDate: new Date(session.uploadedDate).toLocaleString(),
    uploadedBy: String(session.uploadedBy),
    UploadedByFullName: session.uploadedByFullName || "",
    UploadedByRoleId: session.uploadedByRoleId || "",
    bitmap: session.bitmap || 0,
    violationsBlobFile: session.violationsBlobFile || null,
    // Default values for CDR analysis data
    InvalidPhoneNumberCount: 0,
    totalCalls: 0,
    connectedCalls: 0,
    durationViolations: [],
    callingWindowViolations: 0,
    reassignedNumberCalls: 0,
    sipCodeDistribution: {},
    callDispositions: {
      total: 0,
      connected: 0,
      noAnswer: 0,
      voicemail: 0,
      wrongNumber: 0,
      other: 0,
    },
    callsPerDayPerANI: { dates: [], data: [] },
    aiAnalysis: null,
  };

  if (isProcessing) {
    return {
      ...baseSession,
      isProcessing: true,
      averageDuration: "Processing",
      anserRate: "Processing",
      avgDailyCallsPerANI: "Processing",
    };
  }

  if (analysisResults) {
    const totalCalls = analysisResults.TotalCalls || 0;
    const connectedCalls = analysisResults.ConnectedCalls || 0;
    const answerRateNum =
      analysisResults.AnswerRate !== undefined
        ? Number(analysisResults.AnswerRate)
        : 0;
    const answerRateStr = `${(answerRateNum * 100).toFixed(0)}%`;
    const avgDailyCallsPerANIStr =
      analysisResults.AverageDailyCallsPerANI !== undefined
        ? String(analysisResults.AverageDailyCallsPerANI)
        : "N/A";

    const invalidPhoneNumberCount =
      analysisResults.InvalidPhoneNumberCount || 0;
    const callDispositionCounts = analysisResults.CallDispositionCounts || {};
    const transformedCallDispositions = transformCallDispositions(
      callDispositionCounts,
    );
    const callsPerDayANIFromApi = analysisResults.CallsPerDayANI || {};
    const transformedCallsPerDayANI = transformCallsPerDayANI(
      callsPerDayANIFromApi,
    );

    const durationViolationsArray = getDurationViolations(analysisResults);

    return {
      ...baseSession,
      isProcessing: false,
      InvalidPhoneNumberCount: invalidPhoneNumberCount,
      totalCalls: totalCalls,
      connectedCalls: connectedCalls,
      averageDuration: analysisResults.AverageDuration || "N/A",
      anserRate: answerRateStr,
      avgDailyCallsPerANI: avgDailyCallsPerANIStr,
      durationViolations: durationViolationsArray,
      reassignedNumberCalls: Array.isArray(
        analysisResults.RecentlyReassignedViolations,
      )
        ? analysisResults.RecentlyReassignedViolations.length
        : 0,
      sipCodeDistribution: analysisResults.SIPResponseCodeCounts || {},
      callDispositions: transformedCallDispositions,
      callsPerDayPerANI: transformedCallsPerDayANI,
      aiAnalysis: session.aiAnalysis || null,
    };
  }

  // Fallback case for error states - only override specific values
  return {
    ...baseSession,
    isProcessing: false,
    errorMessage:
      "Failed to retrieve analysis details. Please try refreshing the page.",
    averageDuration: "Error fetching details",
    anserRate: "N/A",
    avgDailyCallsPerANI: "N/A",
  };
};

const CDRAnalysis: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { setCampaignId } = useUploadListContext();
  const [isLoading, setIsLoading] = useState(true);
  const [, setData] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [sessions, setSessions] = useState<CDRSession[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  useEffect(() => {
    // Set the campaign ID in the context when the component mounts
    if (campaignId) {
      setCampaignId(campaignId);
    }
  }, [campaignId, setCampaignId]);

  // Define fetchCDRData outside of useEffect to make it accessible to onDrop
  const fetchCDRData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(""); // Clear previous errors
    try {
      if (!campaignId) {
        setErrorMessage("Campaign ID is not available.");
        setIsLoading(false);
        return;
      }

      // Step 1: Fetch session information
      const allSessionsResponse = await callApi(
        `cdranalysis?campaignId=${campaignId}`,
        HttpMethod.GET,
      );

      if (!allSessionsResponse || !Array.isArray(allSessionsResponse)) {
        console.warn(
          "No sessions found or invalid response from GetAllSessionsFromCampaign",
          allSessionsResponse,
        );
        setSessions([]);
        setData(createDefaultDataStructure([]));
        setIsLoading(false);
        return;
      }

      // Transform the data to match the expected format
      const detailedSessions: CDRSession[] = allSessionsResponse.map(
        (session: any) => transformSessionData(session),
      );

      // Calculate summary data (excluding processing sessions)
      const responseData = createDefaultDataStructure(detailedSessions);

      setData(responseData);
      setSessions(responseData.sessions);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch CDR analysis data:", error);
      setErrorMessage(
        (error as any)?.response?.data?.message ||
          (error as Error).message ||
          "An unexpected error occurred while fetching CDR data.",
      );
      setSessions([]); // Clear sessions on error
      setData(createDefaultDataStructure([]));
      setIsLoading(false);
    }
  }, [campaignId]);

  // Function to fetch and update a single session
  const fetchSingleSession = useCallback(async (sessionId: number) => {
    try {
      console.log(`Fetching updated session data for session: ${sessionId}`);

      // Fetch the specific session details
      const sessionResponse = await callApi(
        `cdranalysis/${sessionId}`,
        HttpMethod.GET,
      );

      if (!sessionResponse) {
        console.error(`No data received for session ${sessionId}`);
        return;
      }

      // Transform the session data using the same logic as fetchCDRData
      const session = sessionResponse;
      const transformedSession = transformSessionData(session);

      // Update the sessions state by replacing the specific session
      setSessions((prevSessions) => {
        const updatedSessions = prevSessions.map((s) =>
          s.sessionId === sessionId ? transformedSession : s,
        );

        // Also update the data state to keep summary in sync
        setData((prevData: any) => {
          if (!prevData) return prevData;

          const updatedData = {
            ...prevData,
            sessions: updatedSessions.sort(
              (a, b) =>
                new Date(b.uploadDate).getTime() -
                new Date(a.uploadDate).getTime(),
            ),
          };

          // Recalculate summary data
          updatedData.summary = calculateSummaryData(updatedSessions);

          return updatedData;
        });

        return updatedSessions;
      });

      console.log(`Successfully updated session ${sessionId}`);
    } catch (error) {
      console.error(`Failed to fetch session ${sessionId}:`, error);
      // Could show a toast notification here if desired
    }
  }, []);

  useEffect(() => {
    if (campaignId) {
      fetchCDRData();
    }
  }, [campaignId, fetchCDRData]);

  // SignalR connection for real-time updates
  const handleSignalRMessage = useCallback(
    (methodName: string, ...args: any[]) => {
      console.log(`Received SignalR message: ${methodName}`, args);

      if (methodName === "NewCdrAnalysis") {
        const [sessionId] = args;
        console.log(`CDR Analysis completed for session: ${sessionId}`);

        // Show notification to user
        toast.success(
          "CDR Analysis completed! Your results are now available.",
          {
            position: "top-right",
            duration: 5000,
          },
        );

        // Refresh the data to show the updated session
        fetchSingleSession(sessionId);
      }
    },
    [fetchSingleSession],
  );

  const signalRHubUrl = `${runtimeConfig.signalRUrl}/cdranalysishub`;

  const { isConnected } = useSignalR({
    hubUrl: signalRHubUrl,
    groupName: campaignId, // Join the campaign-specific group
    onMessage: handleSignalRMessage,
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setErrorMessage("");
    setCurrentFile(file);
    setShowMapping(true);
  }, []);

  const handleCancelMapping = useCallback(() => {
    setShowMapping(false);
    setCurrentFile(null);
  }, []);

  const handleMappingComplete = useCallback(
    async (columnMappings: Record<string, string>, hasHeader: boolean) => {
      if (!currentFile || !campaignId) {
        setErrorMessage("Campaign ID is required for CDR upload.");
        setShowMapping(false);
        setCurrentFile(null);
        return;
      }

      setShowMapping(false);
      const file = currentFile;
      setCurrentFile(null);

      // Generate a GUID and create a modified filename
      const fileExtension =
        file.name.lastIndexOf(".") > -1
          ? file.name.slice(file.name.lastIndexOf("."))
          : "";
      const fileNameWithoutExtension =
        file.name.lastIndexOf(".") > -1
          ? file.name.slice(0, file.name.lastIndexOf("."))
          : file.name;
      const guid = uuidv4();
      const fileNameWithGuid = `${fileNameWithoutExtension}-${guid}${fileExtension}`;

      try {
        // Step 1: Get SAS URL for upload
        const initiateResponse = await callApi(
          "cdranalysis/initiate-upload",
          HttpMethod.POST,
          {
            fileName: fileNameWithGuid,
            fileSize: file.size,
            contentType: file.type,
          },
        );

        if (!initiateResponse?.uploadUrl) {
          setErrorMessage("Failed to get upload URL to Azure Storage");
          throw new Error("Failed to get upload URL");
        }

        // Step 2: Upload file directly to blob storage using SAS URL
        await axios({
          method: "put",
          url: initiateResponse.uploadUrl,
          data: file,
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const total = progressEvent.total || progressEvent.loaded;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total,
            );
            setUploadProgress(percentCompleted);
          },
        });

        console.log("CDR file uploaded successfully");

        try {
          const submitPayload = createSubmitPayload(
            campaignId,
            fileNameWithGuid,
            columnMappings,
            hasHeader,
          );

          console.log(
            "Submitting CDR analysis request with payload:",
            submitPayload,
          );

          try {
            const submitResponse = await callApi(
              "cdranalysis/submit",
              HttpMethod.POST,
              submitPayload,
            );

            if (submitResponse) {
              console.log(
                "CDR Analysis request queued successfully:",
                submitResponse,
              );
            } else {
              console.warn(
                "CDR Analysis submission call completed, but no conclusive response was received.",
              );
            }
          } catch (submitApiError: any) {
            console.error(
              "Error submitting CDR analysis request:",
              submitApiError,
            );
            setErrorMessage(
              submitApiError.data?.message ||
                "Your file was uploaded, but an error occurred while queuing it for analysis. Please contact support if the issue persists.",
            );
            return;
          }
        } catch (submitError: any) {
          console.error("Error submitting CDR analysis request:", submitError);
          setErrorMessage(
            submitError.response?.data?.message ||
              "Your file was uploaded, but an error occurred while queuing it for analysis. Please contact support if the issue persists.",
          );
          return;
        }

        setUploadProgress(0);
        fetchCDRData();
      } catch (error: any) {
        console.error("There was an error uploading the CDR file.", error);
        setUploadProgress(0);
        setErrorMessage(
          error.response?.data?.message ||
            "There was an error uploading your CDR file. Please try again or contact support if the issue persists.",
        );
      }
    },
    [currentFile, campaignId, fetchCDRData],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <h1 className="mb-0">Call Detail Records Analysis</h1>
            <ToolTip
              description="Call Detail Records (CDRs) are data records produced by telecommunications systems that document the details of phone calls including phone numbers, call times, durations, and outcomes. Analyzing CDRs helps avoid spam call labeling, optimize campaign performance, and ensure compliance."
              placement="bottom"
            />
            {/* Real-time connection status - only show when disconnected */}
            {!isConnected && (
              <div className="ms-3">
                <div className="alert alert-warning d-flex align-items-center mb-0 py-2 px-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <span className="me-2">
                    Live updates have been interrupted. Refresh to view the
                    latest.
                  </span>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            )}
          </div>
          <p>
            Upload your Call Detail Records (CDRs) to be analyzed to ensure
            compliance, reduce spam call labeling, and optimize contact rates.
          </p>

          {/* Mapping UI or Upload + Sessions */}
          {showMapping && currentFile ? (
            <CDRMapping
              file={currentFile}
              onMappingComplete={handleMappingComplete}
              onCancel={handleCancelMapping}
            />
          ) : (
            <>
              {/* File Upload Section */}
              <div className="row mb-4">
                <div className="col-md-12 col-lg-6">
                  {uploadProgress > 0 && (
                    <ProgressBar progress={uploadProgress} />
                  )}
                  {errorMessage && <Warning message={errorMessage} />}
                  <div className="d-flex gap-2 mt-4 flex-wrap">
                    <FileUploadContainer
                      getRootProps={getRootProps}
                      getInputProps={getInputProps}
                      isDragActive={isDragActive}
                      title="UPLOAD CDR FILE"
                      iconClassName="bi-box-arrow-up"
                    />
                  </div>
                </div>

                {/* File format instructions */}
                <div className="col-md-12 col-lg-6">
                  <CollapsibleCard
                    title="CDR File Format Instructions"
                    icon="bi-info-circle"
                    defaultExpanded={true}
                    className="rounded"
                  >
                    <p>
                      Only upload outbound Call Detail Records. Your uploaded
                      file can be either CSV or XLSX. Your Call Details Record
                      (CDR) file should include the following columns:
                    </p>
                    <ul>
                      <li>
                        <strong>Phone Number</strong> - The phone number dialed
                      </li>
                      <li>
                        <strong>ANI</strong> - Automatic Number Identification
                        (Caller ID used to place the call)
                      </li>
                      <li>
                        <strong>Call Start Time</strong> - When the call began
                      </li>
                      <li>
                        <strong>Call End Time</strong> - When the call ended
                      </li>
                      <li>
                        <strong>Call Disposition</strong> - Agent-entered
                        outcome of the call
                      </li>
                      <li>
                        <strong>Carrier SIP Response Code</strong> - Response
                        code from the carrier
                      </li>
                      <li>
                        <strong>Call Length</strong> - Duration in HH:MM:SS
                        format or integer seconds.
                      </li>
                    </ul>
                    <p>For large datasets, ZIP compression is supported.</p>
                    <p>
                      Need help? Contact our Help Desk for assistance with
                      formatting your CDR files.
                    </p>
                    <div className="mt-3">
                      <a
                        href="/fileformats/CDR - EXAMPLE FORMAT.csv"
                        download
                        className="col-md-12 btn btn-primary btn-sm"
                      >
                        <i className="bi bi-file-earmark-spreadsheet"></i>
                        Download Example File
                      </a>
                    </div>
                  </CollapsibleCard>
                </div>
              </div>

              {/* Session List */}
              {sessions.length > 0 ? (
                <Sessions sessions={sessions} />
              ) : (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No CDR data available for this campaign. Please upload a CDR
                  file to begin analysis.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CDRAnalysis;
